/**
 * Client Portal Auth API
 * Email + password authentication for athlete portal access
 */

import type { ClientUser, MagicLinkResponse, LoginResponse } from '../types/clientPortal'
import { previewState } from './clientPortal/previewMode'

// Separate auth store key for client portal
const CLIENT_AUTH_KEY = 'pocketbase_client_auth'

/**
 * Login with email and password
 * Calls PocketBase directly via raw fetch to avoid overwriting the agent's pb.authStore
 */
export async function loginWithPassword(email: string, password: string): Promise<LoginResponse> {
  const pocketbaseUrl = (import.meta.env.VITE_POCKETBASE_URL || '').replace(/\/$/, '')
  const res = await fetch(`${pocketbaseUrl}/api/collections/client_users/auth-with-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identity: email, password }),
  })
  const data = await res.json()
  if (!res.ok) {
    return { success: false, error: data.message || 'Invalid email or password' }
  }
  saveClientAuth(data.token, data.record as ClientUser)
  return { success: true, token: data.token, user: data.record as ClientUser }
}

/**
 * Request a sign-in link. The athlete clicks it and is in — no password.
 *
 * Kept as an alias below under its old name so nothing breaks mid-rename, but
 * the honest name is this one: the endpoint no longer sends a password.
 */
export async function requestMagicLink(email: string): Promise<MagicLinkResponse> {
  const res = await fetch('/api/portal/magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  const data = await res.json()
  return {
    success: res.ok,
    message: res.ok ? 'A new password has been sent to your email.' : (data.error || 'Failed to reset password'),
  }
}

/**
 * Get the current client session from local storage.
 *
 * While an agent is previewing a client's portal there is no portal session
 * in this browser at all: the session is the agent's own token paired with
 * the athlete's real account record, and every read goes through the preview
 * proxy, which decides what that token may see. See api/clientPortal/previewMode.
 */
export function getClientSession(): { token: string; user: ClientUser } | null {
  const preview = previewState()
  if (preview) return { token: preview.agentToken, user: preview.user }

  try {
    const stored = localStorage.getItem(CLIENT_AUTH_KEY)
    if (!stored) return null

    const { token, user, expires } = JSON.parse(stored)

    // Check if session has expired (7 days)
    if (expires && new Date(expires) < new Date()) {
      clearClientAuth()
      return null
    }

    return { token, user }
  } catch {
    clearClientAuth()
    return null
  }
}

/**
 * Check if client is currently authenticated
 */
export function isClientAuthenticated(): boolean {
  return getClientSession() !== null
}

/**
 * Get the current authenticated client user
 */
export function getClientUser(): ClientUser | null {
  const session = getClientSession()
  return session?.user ?? null
}

/**
 * Get the client auth token for API requests
 */
export function getClientToken(): string | null {
  const session = getClientSession()
  return session?.token ?? null
}

/**
 * Save client auth to local storage.
 *
 * Exported as setClientSession for the sign-in link an agent texts: that
 * session is minted by the server (api/portal/start), so the portal has a
 * token and a record without ever having had a password to send.
 */
export function setClientSession(token: string, user: ClientUser): void {
  saveClientAuth(token, user)
}

function saveClientAuth(token: string, user: ClientUser): void {
  const expires = new Date()
  expires.setDate(expires.getDate() + 7) // 7 day expiry

  localStorage.setItem(
    CLIENT_AUTH_KEY,
    JSON.stringify({
      token,
      user,
      expires: expires.toISOString(),
    })
  )
}

/**
 * Clear client auth from local storage
 */
export function clearClientAuth(): void {
  localStorage.removeItem(CLIENT_AUTH_KEY)
  // Written by older builds of lib/clientPocketbase.ts and never cleared:
  // a copy of the portal token (or, after a preview, the agent's).
  localStorage.removeItem('reppr_portal_pb_auth')
  // The push endpoint saved for this athlete must not be reused by the next
  // person who signs in on this device. Key shared with clientPortal/pushSubscriptions.
  localStorage.removeItem('reppr_push_endpoint')
  // Notify other tabs
  localStorage.setItem('client_portal_logout', Date.now().toString())
  setTimeout(() => localStorage.removeItem('client_portal_logout'), 100)
}

/**
 * Logout the client user
 */
export async function clientLogout(): Promise<void> {
  clearClientAuth()
}

/**
 * Refresh the client user data
 * Returns current session user — session data is set at login and persisted in localStorage.
 */
export async function refreshClientUser(): Promise<ClientUser | null> {
  const session = getClientSession()
  return session?.user ?? null
}

/** @deprecated Use requestMagicLink — the endpoint sends a link, not a password. */
export const requestPasswordReset = requestMagicLink
